/**
 * Centralized Logger Utility for Frontend
 * Environment-aware logging with performance optimizations
 */

const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';

// Log levels in order of severity
const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3,
  TRACE: 4
};

// Determine current log level based on environment
const getCurrentLogLevel = () => {
  if (isProduction) {
    // Production: Only ERROR and WARN
    return LOG_LEVELS.WARN;
  }
  // Development: All levels
  return LOG_LEVELS.TRACE;
};

const currentLogLevel = getCurrentLogLevel();

/**
 * Check if a log level should be output
 */
const shouldLog = (level) => {
  return level <= currentLogLevel;
};

/**
 * Lazy evaluation helper - only evaluate function if logging is enabled
 */
const evaluateArgs = (args, level) => {
  if (!shouldLog(level)) {
    return [];
  }
  // Evaluate functions and return their results
  return args.map(arg => {
    if (typeof arg === 'function') {
      try {
        return arg();
      } catch (error) {
        return `[Error evaluating log argument: ${error.message}]`;
      }
    }
    return arg;
  });
};

/**
 * Logger object with methods for each log level
 */
const logger = {
  /**
   * Log error messages (always enabled)
   */
  error: (...args) => {
    if (shouldLog(LOG_LEVELS.ERROR)) {
      console.error('[ERROR]', ...evaluateArgs(args, LOG_LEVELS.ERROR));
    }
  },

  /**
   * Log warning messages (enabled in production)
   */
  warn: (...args) => {
    if (shouldLog(LOG_LEVELS.WARN)) {
      console.warn('[WARN]', ...evaluateArgs(args, LOG_LEVELS.WARN));
    }
  },

  /**
   * Log info messages (development only)
   */
  info: (...args) => {
    if (shouldLog(LOG_LEVELS.INFO)) {
      console.info('[INFO]', ...evaluateArgs(args, LOG_LEVELS.INFO));
    }
  },

  /**
   * Log debug messages (development only)
   */
  debug: (...args) => {
    if (shouldLog(LOG_LEVELS.DEBUG)) {
      console.log('[DEBUG]', ...evaluateArgs(args, LOG_LEVELS.DEBUG));
    }
  },

  /**
   * Log trace messages (development only)
   */
  trace: (...args) => {
    if (shouldLog(LOG_LEVELS.TRACE)) {
      console.log('[TRACE]', ...evaluateArgs(args, LOG_LEVELS.TRACE));
    }
  },

  /**
   * Check if a log level is enabled (for conditional logic)
   */
  isEnabled: (level) => {
    const levelMap = {
      error: LOG_LEVELS.ERROR,
      warn: LOG_LEVELS.WARN,
      info: LOG_LEVELS.INFO,
      debug: LOG_LEVELS.DEBUG,
      trace: LOG_LEVELS.TRACE
    };
    return shouldLog(levelMap[level] ?? LOG_LEVELS.TRACE);
  },

  /**
   * Get current environment
   */
  getEnvironment: () => {
    return isProduction ? 'production' : 'development';
  }
};

export default logger;
